// nav-context.tsx — the redesign's URL-driven navigation contract.
//
// The Drip Campaigns redesign mandates that ALL internal navigation be
// URL-driven: a click that opens a board/template editor must `navigate()` to
// the editor's own route (so reload + back/forward restore it), NOT flip a
// component-state / Redux flag in place (the legacy `activeBulkView` swap, which
// is lost on reload and decided by component state).
//
// Reused leaf components (BulkAudienceSelectRow, TemplateCard, …) live deep in
// the legacy bt-bulk-dashboard tree and are also mounted OUTSIDE the redesign
// (the legacy /launch-campaign flow, the extension). Rather than prop-thread a
// navigator through that whole tree, the redesign shell provides this context;
// every reused leaf reads it via `useCampaignsRedesignNav()` and:
//   • if a provider is present (rendered under the redesign shell) → navigate()
//   • if it is null (legacy / extension callers) → fall through to the existing
//     Redux/thunk behaviour, byte-for-byte unchanged.
//
// So the context is intentionally NULLABLE and every call site guards on it.

import React from 'react';

export interface CampaignsRedesignNav {
  /** Open an audience board's contacts editor → /campaigns/audiences/:boardId/edit */
  openBoard(boardId: string): void;
  /** Open a template editor → /campaigns/templates/:templateId/edit */
  openTemplate(templateId: string): void;
  /** Open a campaign's message-logs view → /campaigns/:campaignId/logs */
  openLogs(campaignId: string): void;
  /** Open a campaign (drip) in the wizard → /campaigns/:id/edit/:step
   *  (step defaults to 'audience'). Used by the detail actions menu for
   *  Edit and after Duplicate. */
  editCampaign(dripId: string, step?: string): void;
  /** Create a new template → /campaigns/templates/new */
  newTemplate(): void;
  /** Open the New-Audience import modal → /campaigns?tab=audiences&new=audience.
   *  The modal is an overlay (not a full surface), so its open state lives as a
   *  URL flag the Audiences tab reads; closing the modal clears the flag. */
  newAudience(): void;
  /** Leave a board sub-editor → back to the Audiences tab list */
  closeBoard(): void;
  /** Leave a template sub-editor → back to the Templates tab list */
  closeTemplate(): void;
}

const Ctx = React.createContext<CampaignsRedesignNav | null>(null);

export const CampaignsRedesignNavProvider = Ctx.Provider;

export const useCampaignsRedesignNav = (): CampaignsRedesignNav | null =>
  React.useContext(Ctx);

// ── Sub-editor detour detection ─────────────────────────────────────────────
// The wizard reclaims empty throwaway drafts when it unmounts — "exit" is EVERY
// way of leaving the wizard route (see wizard.tsx). But opening a board or
// template SUB-EDITOR (/campaigns/audiences/:id/edit, /campaigns/templates/…)
// is a round-trip DETOUR, not an exit: the user returns via Back. Deleting the
// empty draft on that detour means Back lands on a now-deleted draft → the
// wizard re-fetches nothing → permanent "Loading…".
//
// The shell records the LIVE destination pathname here on every render (it
// re-renders with the new location BEFORE the outgoing wizard's unmount cleanup
// runs), so the wizard's cleanup can tell a sub-editor detour from a real exit
// by WHERE we navigated — not a flag that can go stale across unrelated nav.
let campaignsDestinationPath = '';

/** Called by the redesign shell each render with the current location pathname. */
export const recordCampaignsPath = (pathname: string): void => {
  campaignsDestinationPath = pathname;
};

/** True for a board/template sub-editor route (round-trip from the wizard). */
export const isCampaignsSubEditorPath = (pathname: string): boolean =>
  // The optional `clear-` prefix is the white-labelled flow's copy of these
  // routes. Matched here rather than via flowPath() because this is a pure
  // function called with arbitrary pathnames (including in tests), so it must
  // not depend on where the window currently is.
  /^\/(clear-)?campaigns\/(audiences|templates)\//.test(pathname);

/** Whether the most recent navigation is INTO a sub-editor (a detour, not an exit). */
export const isNavigatingToSubEditor = (): boolean =>
  isCampaignsSubEditorPath(campaignsDestinationPath);
